/**
 * In-SPA voice controller (R8.1, R8.4). Captures microphone audio and streams
 * it over the socket to the Voice Gateway, and exposes a switch-to-text control.
 * The microphone and audio playback are behind ports so the controller is
 * testable without a browser audio stack.
 */
export interface MicrophoneCapture {
  start(onChunk: (chunk: Uint8Array) => void): Promise<void>;
  stop(): Promise<void>;
}

export interface AudioSink {
  play(audio: Uint8Array): Promise<void>;
}

export interface AudioStreamTransport {
  sendAudioChunk(chunk: Uint8Array): Promise<void>;
}

export class VoiceController {
  private capturing = false;

  constructor(
    private readonly mic: MicrophoneCapture,
    private readonly transport: AudioStreamTransport,
    private readonly sink: AudioSink,
    private readonly onSwitchToText: () => void,
  ) {}

  isCapturing(): boolean {
    return this.capturing;
  }

  async startSpeaking(): Promise<void> {
    if (this.capturing) return;
    this.capturing = true;
    await this.mic.start((chunk) => {
      void this.transport.sendAudioChunk(chunk);
    });
  }

  async stopSpeaking(): Promise<void> {
    if (!this.capturing) return;
    this.capturing = false;
    await this.mic.stop();
  }

  async playResponse(audio: Uint8Array): Promise<void> {
    await this.sink.play(audio);
  }

  /** User chose to type instead; stop capture and notify the view. */
  async switchToText(): Promise<void> {
    await this.stopSpeaking();
    this.onSwitchToText();
  }
}
